import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Clock, Flame, Truck, Package, XCircle, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { useCart } from '@/context/CartContext';
import { formatGs, ORDER_STATUS } from '@/utils/helpers';

const STEPS = [
  { key: 'pendiente', icon: Clock, label: 'Pendiente' },
  { key: 'cocina', icon: Flame, label: 'En cocina' },
  { key: 'en_camino', icon: Truck, label: 'En camino' },
  { key: 'entregado', icon: Package, label: 'Entregado' },
];

export default function OrderTrackingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, branchId, zone } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repeating, setRepeating] = useState(false);

  const fetchOrder = () => {
    api.get(`/pedidos/${id}`).then(r => {
      setOrder(r.data.pedido);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(fetchOrder, 15000);
    return () => clearInterval(interval);
  }, [id]); // eslint-disable-line

  if (loading) return (
    <div className="pb-nav p-4 space-y-4">
      {[1,2,3].map(i => <div key={i} className="h-20 bg-zinc-900 rounded-xl animate-pulse" />)}
    </div>
  );

  if (!order) return (
    <div className="pb-nav p-8 text-center text-zinc-500">Pedido no encontrado.</div>
  );

  const isCancelled = order.status === 'cancelado' || order.status === 'rechazado';
  const currentStep = ORDER_STATUS[order.status]?.step ?? 0;
  const canRepeat = ['entregado', 'cancelado', 'rechazado'].includes(order.status);

  const handleRepeat = async () => {
    if (!zone) {
      toast.error('Seleccioná tu ubicación primero para repetir el pedido.');
      return;
    }
    setRepeating(true);
    try {
      const { data } = await api.get(`/pedidos/${id}/repetir?branch_id=${branchId}`);
      let added = 0;
      for (const item of data.disponibles) {
        addItem(
          { id: item.product_id, name: item.name, image_url: item.image_url },
          item.quantity,
          item.addons,
          item.unit_price
        );
        added++;
      }
      if (data.no_disponibles.length > 0) {
        const names = data.no_disponibles.map(i => i.product_name).join(', ');
        toast.warning(`No disponible: ${names}`, { duration: 4000 });
      }
      if (added > 0) {
        toast.success(`${added} producto${added > 1 ? 's' : ''} agregado${added > 1 ? 's' : ''} al carrito`);
        navigate('/carrito');
      } else {
        toast.error('Ningún producto del pedido está disponible actualmente.');
      }
    } catch {
      toast.error('Error al repetir el pedido.');
    } finally {
      setRepeating(false);
    }
  };

  return (
    <div data-testid="order-tracking-page" className="pb-nav">
      <div className="px-4 pt-4 flex items-center gap-3">
        <button data-testid="tracking-back-btn" onClick={() => navigate('/mis-pedidos')} className="w-10 h-10 rounded-full bg-zinc-900 flex items-center justify-center hover:bg-zinc-800 transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h2 className="text-xl font-bold">Pedido #{order.id}</h2>
          <p className="text-xs text-zinc-500">{new Date(order.created_at).toLocaleString('es-PY')}</p>
        </div>
      </div>

      {/* Status timeline */}
      {!isCancelled ? (
        <div className="mt-6 px-4">
          <div className="flex items-center justify-between relative">
            <div className="absolute top-5 left-[10%] right-[10%] h-0.5 bg-zinc-800" />
            <div className="absolute top-5 left-[10%] h-0.5 bg-[#E84C1C] transition-all duration-500" style={{ width: `${Math.min(currentStep / 3, 1) * 80}%` }} />
            {STEPS.map((s, i) => {
              const done = currentStep >= i;
              const active = currentStep === i;
              return (
                <div key={s.key} className="relative z-10 flex flex-col items-center gap-1.5 w-1/4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${done ? 'bg-[#E84C1C] text-white' : 'bg-zinc-800 text-zinc-500'} ${active ? 'ring-4 ring-[#E84C1C]/20 animate-pulse-brand' : ''}`}>
                    {done && i < currentStep ? <CheckCircle2 size={20} /> : <s.icon size={18} />}
                  </div>
                  <span className={`text-[10px] font-medium ${done ? 'text-zinc-200' : 'text-zinc-600'}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mt-6 mx-4 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3">
          <XCircle size={24} className="text-red-400" />
          <div>
            <p className="font-medium">{ORDER_STATUS[order.status]?.label || order.status}</p>
            <p className="text-xs text-zinc-400">Este pedido fue {order.status}</p>
          </div>
        </div>
      )}

      {/* Branch + Pickup/Delivery info */}
      <div className="mt-6 mx-4 p-4 rounded-xl bg-zinc-900 border border-zinc-800/50">
        <p className="text-xs text-zinc-500">Sucursal</p>
        <p className="text-sm font-medium">{order.branch_name}</p>
        {order.order_type === 'pickup' && (
          <div data-testid="tracking-pickup-info" className="mt-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <p className="text-xs font-medium text-amber-400">Retiro en local</p>
            {order.pickup_ready_at && (
              <p className="text-sm text-amber-300 mt-0.5">Hora estimada: {new Date(order.pickup_ready_at).toLocaleTimeString('es-PY', { hour: '2-digit', minute: '2-digit' })}</p>
            )}
          </div>
        )}
        {order.order_type !== 'pickup' && order.address_line && (
          <>
            <p className="text-xs text-zinc-500 mt-2">Dirección de entrega</p>
            <p className="text-sm">{order.address_line}</p>
          </>
        )}
        {order.order_type !== 'pickup' && order.delivery_address_text && (
          <>
            <p className="text-xs text-zinc-500 mt-2">Dirección detallada</p>
            <p className="text-sm text-zinc-300">{order.delivery_address_text}</p>
          </>
        )}
      </div>

      {/* Items */}
      <div className="mt-4 mx-4 p-4 rounded-xl bg-zinc-900 border border-zinc-800/50 space-y-2">
        <p className="text-xs text-zinc-500 uppercase tracking-wider">Productos</p>
        {(order.items || []).map(item => (
          <div key={item.id} className="flex justify-between text-sm">
            <span className="text-zinc-300">{item.quantity}x {item.product_name}</span>
            <span>{formatGs(item.subtotal)}</span>
          </div>
        ))}
        <div className="border-t border-zinc-800 pt-2 mt-1 space-y-1">
          <div className="flex justify-between text-sm text-zinc-400">
            <span>Subtotal</span><span>{formatGs(order.subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm text-zinc-400">
            <span>Envío</span><span>{formatGs(order.delivery)}</span>
          </div>
          <div className="flex justify-between font-semibold">
            <span>Total</span><span className="text-[#E84C1C]">{formatGs(order.total)}</span>
          </div>
        </div>
      </div>

      {order.nota_general && (
        <div className="mt-4 mx-4 p-3 rounded-xl bg-zinc-900 border border-zinc-800/50">
          <p className="text-xs text-zinc-500">Nota</p>
          <p className="text-sm text-zinc-300">{order.nota_general}</p>
        </div>
      )}

      {canRepeat && (
        <div className="mt-6 mx-4 mb-4">
          <button
            data-testid="repeat-order-btn"
            onClick={handleRepeat}
            disabled={repeating}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#E84C1C] text-white font-semibold hover:bg-[#d43f12] transition-all disabled:opacity-50"
          >
            {repeating ? <Loader2 size={18} className="animate-spin" /> : <RotateCcw size={18} />}
            Repetir pedido
          </button>
        </div>
      )}
    </div>
  );
}
